import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { VerticalCard } from '../../partials/cards/verticalCard/VerticalCard';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { LARGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { CardComponentDataProps } from '../../partials/cards/cards.utils';
import { createImageWidths } from '../../partials/image/image.utils';
import { LayoutComponentBaseProps, getLayoutComponentPadding } from '../pageLayouts/layout.utils';

const createStyles = (baseUnit: BaseUnit) => StyleSheet.create({
  listWrapper: {
    display: 'grid',
    maxWidth: `${LARGE_SCREEN_SIZE}px`,
  },
  scrollContainer: {
    '-webkit-overflow-scrolling': 'touch',
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    overflowY: 'visible',
    [MEDIA_BREAKPOINTS.large]: {
      overflow: 'visible',
      justifyContent: 'center',
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gridColumnGap: `${baseUnit / 2}px`,
      display: 'grid',
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingBottom: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingBottom: `${baseUnit}px`,
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  scrollItem: {
    flex: '0 0 auto',
    flexBasis: `${300 - baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0,
      },
      ':last-child': {
        marginRight: 0,
      },
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0,
      },
      ':last-child': {
        marginRight: 0,
      },
    },
    position: 'relative',
    boxSizing: 'border-box',
    '@media (max-width: 330px)': {
      flexBasis: 'min(85%, 255px)',
    },
  },
});

interface ScrollableArticleListComponentDataProps extends LayoutComponentBaseProps {
  cardsProps: CardComponentDataProps[];
}

interface ScrollableArticleListComponentThemeProps {
  baseUnit: BaseUnit;
}

const imageWidths = createImageWidths(360, 360, 360);

export const ScrollableArticleList: React.FunctionComponent<ScrollableArticleListComponentDataProps> = ({ cardsProps, paddingFactors }) => {
  const { baseUnit } = useTheme<ScrollableArticleListComponentThemeProps>();
  const styles = createStyles(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);

  const createCard = (props: CardComponentDataProps) => {
    const { id } = props;
    return (
      <div className={css(styles.scrollItem)} key={id}>
        <VerticalCard {...props} imageWidths={imageWidths} />
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className={css(paddingStyle, styles.listWrapper)}>
        <div className={css(styles.scrollContainer)}>
          {cardsProps.map(createCard)}
        </div>
      </div>
    </ErrorBoundary>
  );
};
